import React, { useEffect, useRef, useState } from 'react';
import { Link, useOutletContext, useSearchParams } from 'react-router-dom';
import ModuleFrame, { Chip, KV, useModuleTab } from '../../components/ModuleFrame';
import { AppFocusChips, useSelectedApp, useAppFocusSet, inFocus, withApp } from '../../components/AppSelector';
import AskPane from '../../components/AskPane';
import { createEntity, updateEntity, useEntityList, friendlyError } from '../../data/entities';
import { useCan } from '../../data/permissions';
import { savePolicy } from '../../data/policy';
import PolicyHistory from './PolicyHistory';
import { apiGet, apiPost } from '../../utils/backendApi';
import { uploadFile, buildKey, isUploadConfigured } from '../../data/uploadService';
import { useAuth } from '../../AuthContext';
import ConnectorEditor, { NewConnector, ProviderCards, SyncChip, useConnectorProviders } from '../../components/ConnectorEditor';
import { LibraryLifecycle } from '../../components/LifecycleRail';

// Knowledge Platform — org-scoped documents with a lifecycle
// (draft → uat → approved → live → retired). Publishing is a backend action
// that mirrors the approved doc into the retrieval corpus with orgId, so
// NoVA answers come from THIS org's approved knowledge only. Guardrail
// policies live on the org doc and are enforced in retrieval ask().

const STATUS_TONE = { draft: 'muted', uat: 'info', approved: 'ok', live: 'ok', retired: 'danger' };
const GUARDRAILS = [
  ['approvedSourcesOnly', 'Approved sources only', 'Answers draw only from this org\'s published knowledge — never the platform corpus.'],
  ['citationsRequired', 'Citations required', 'An answer without a citation is refused and deflected.'],
  ['sensitiveDataMasking', 'Sensitive data masking', 'Emails, phone numbers, card and ID numbers are redacted in answers.'],
  ['uncertainToReview', 'Uncertain → Human Review', 'Low-confidence or unanswerable questions file a review item instead of guessing.'],
];

export default function KnowledgeModule() {
  const { org, myRole } = useOutletContext();
  const { user } = useAuth();
  const isAdmin = ['admin', 'owner'].includes(myRole);
  const can = useCan(org, myRole);
  const canEdit = can('knowledge.publish'); // approve, publish, retire; guardrails stay admin (org doc)
  const canConnect = can('integration.update'); // connectors are integrations of kind knowledge
  const { app } = useSelectedApp();
  const { focus } = useAppFocusSet();
  const [tab, setTab] = useModuleTab('sources');
  const { docs: all } = useEntityList('knowledgeDoc', { org: org.id });
  const { docs: reviewItems } = useEntityList('reviewItem', { org: org.id });
  const { docs: integrations } = useEntityList('integration', { org: org.id });
  const providers = useConnectorProviders(org.id);
  const [index, setIndex] = useState({});
  const [adding, setAdding] = useState(false);
  const [addingConnector, setAddingConnector] = useState(null); // provider key
  const [openConnector, setOpenConnector] = useState(null);     // integration id
  const connectors = (integrations || []).filter((i) => i.kind === 'knowledge');

  const docs = all || [];
  // With a focus: documents linked to the apps in focus, then the shared
  // ones (linked to no app). Without: every document in the org.
  const linkedDocs = docs.filter((d) => focus && inFocus(focus, d.appIds));
  const sharedDocs = docs.filter((d) => !(d.appIds || []).length);
  // `?doc=<id>` (a console-search hit) lands on that row, open and marked —
  // even when the app focus would otherwise hide it.
  const [params] = useSearchParams();
  const target = params.get('doc') || '';
  const targetDoc = target ? docs.find((d) => d.id === target) : null;
  const focused = focus ? [...linkedDocs, ...sharedDocs] : docs;
  const lead = targetDoc && !focused.includes(targetDoc) ? [targetDoc] : [];
  const shown = [...lead, ...focused];
  const live = docs.filter((d) => d.status === 'live');
  const awaiting = docs.filter((d) => d.status === 'uat');
  const queue = (reviewItems || []).filter((r) => r.status === 'pending' && r.queue === 'knowledge');

  const refreshIndex = React.useCallback(() => {
    apiGet(`/knowledge/${org.id}/index-status`).then((r) => setIndex(r.docs || {})).catch(() => {});
  }, [org.id]);
  useEffect(() => { refreshIndex(); }, [refreshIndex, live.length]);
  // The chips come from the backend, not Firestore, so they would otherwise
  // sit on "pending" after the index worker finishes (audit P1-3): poll
  // every 30 s while any live doc is still pending, and when a doc's own
  // indexStatus flips (the entity list is realtime).
  const indexSig = live.map((d) => `${d.id}:${d.indexStatus || ''}`).join(',');
  const anyPending = live.some((d) => !index[d.id] || !['done', 'indexed', 'error'].includes(index[d.id]?.indexStatus));
  useEffect(() => { refreshIndex(); }, [refreshIndex, indexSig]);
  useEffect(() => {
    if (!anyPending) return undefined;
    const t = setInterval(() => { if (document.visibilityState === 'visible') refreshIndex(); }, 30000);
    return () => clearInterval(t);
  }, [anyPending, refreshIndex]);

  const liveVersion = live.length ? `v${live.reduce((s, d) => s + (d.publishedVersion || d.version || 1), 0)}.${live.length}` : '—';

  return (
    <ModuleFrame
      title="Knowledge Platform"
      subtitle="Sources · Versions & approvals · Guardrails"
      accent="#a78bfa"
      note={`Knowledge, tab ${tab}${app ? `, app "${app.name}"` : ''}: ${live.length} live docs (${liveVersion}), ${awaiting.length} awaiting approval, ${queue.length} in the knowledge review queue. NoVA: create_knowledgeDoc, add_knowledge_documents, publish_knowledge_doc, list_knowledgeDoc.`}
      help="Paste, upload or link documents; move them through UAT and approval; publish them into the answer corpus. Every published chunk carries this org's id — other orgs can never retrieve it."
      context={(
        <>
          <AppFocusChips multi />
          <Chip tone={live.length ? 'ok' : 'muted'}>{live.length} live · {liveVersion}</Chip>
          {awaiting.length > 0 && <Chip tone="warn">{awaiting.length} awaiting approval</Chip>}
        </>
      )}
      lead={app ? <LibraryLifecycle org={org} app={app} moduleId="knowledge" /> : null}
      tabs={[
        { id: 'sources', label: 'Sources', count: docs.length },
        { id: 'approvals', label: 'Versions & approvals', count: awaiting.length + queue.length },
        { id: 'guardrails', label: 'Guardrails' },
      ]}
      tab={tab}
      onTab={setTab}
      verbs={[
        { label: '+ Add source', hint: 'paste · upload · URL', onClick: () => { setTab('sources'); setAdding(true); }, primary: true },
        { label: 'Review approvals', hint: `${awaiting.length} waiting`, onClick: () => setTab('approvals') },
        { label: 'Test guardrails', hint: 'ask with policies applied', onClick: () => setTab('guardrails') },
      ]}
    >
      {tab === 'sources' && (
        <ModuleFrame.Columns>
          <div className="mf-span-2" style={{ display: 'grid', gap: 16 }}>
            {(adding || !docs.length) && (
              <AddSource orgId={org.id} uid={user.uid} appId={app?.id} onDone={() => setAdding(false)} />
            )}
            <ModuleFrame.Panel title="Documents" meta={focus ? `${linkedDocs.length} linked to ${focus.length > 1 ? `${focus.length} apps` : app.name} · ${sharedDocs.length} shared` : `${docs.length} in this organization`}>
              {!docs.length && <p className="cs-help">No sources yet. Add the first one above.</p>}
              {focus && docs.length > 0 && !linkedDocs.length && <p className="cs-help">Nothing is linked to {app.name} yet — link a shared document below, or add one.</p>}
              <ul className="mf-list">
                {shown.map((d, idx) => (
                  <React.Fragment key={d.id}>
                    {focus && idx === lead.length + linkedDocs.length && <li className="mf-muted" style={{ fontSize: 11, margin: '6px 0 2px' }}>Shared — not linked to an app</li>}
                    <DocRow orgId={org.id} doc={d} index={index[d.id]} canEdit={canEdit} uid={user.uid} app={app} onChanged={refreshIndex} target={d.id === target} />
                  </React.Fragment>
                ))}
              </ul>
            </ModuleFrame.Panel>
          </div>
          <ModuleFrame.Panel title="Connectors" meta={connectors.length ? `${connectors.length} connected · sync on a schedule, keep versions` : 'sync a source on a schedule, keep versions'}>
            {addingConnector && providers && (
              <NewConnector orgId={org.id} uid={user.uid} provider={addingConnector} providers={providers} appId={app?.id}
                onDone={(id) => { setAddingConnector(null); if (id) setOpenConnector(id); }} />
            )}
            {connectors.length > 0 && (
              <ul className="mf-list" style={{ marginBottom: 10 }}>
                {connectors.map((c) => (
                  <li key={c.id} className="mf-row kc-connected" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <button type="button" className="mf-row-grow" style={{ background: 'none', border: 0, color: 'var(--text)', textAlign: 'left', cursor: 'pointer', fontWeight: 600, padding: 0 }} onClick={() => setOpenConnector((v) => (v === c.id ? null : c.id))}>
                        {c.name} <span className="mf-muted">{providers?.[c.provider]?.label || c.provider} · {c.schedule || 'daily'}{c.enabled === false ? ' · off' : ''}</span>
                      </button>
                      <SyncChip integ={c} />
                    </div>
                    {openConnector === c.id && <ConnectorEditor orgId={org.id} integ={c} providers={providers} canEdit={canConnect} app={app} compact />}
                  </li>
                ))}
              </ul>
            )}
            <ProviderCards providers={providers} canConnect={canConnect} onPick={setAddingConnector} />
            <p className="mf-muted" style={{ marginTop: 10 }}>{canConnect ? 'Set up takes a minute: name it, paste the credential, Sync now.' : 'An admin (integration.update) sets connectors up.'} Documents arrive as drafts; changed documents get a new version.</p>
          </ModuleFrame.Panel>
        </ModuleFrame.Columns>
      )}

      {tab === 'approvals' && (
        <ModuleFrame.Columns>
          <ModuleFrame.Panel title="Awaiting approval" meta="status UAT → approve (admin) → publish">
            {!awaiting.length && <p className="cs-help">Nothing waiting. Submit a draft from Sources.</p>}
            {awaiting.length > 0 && <p className="cs-help">A document in UAT is <strong>not searchable yet</strong> — Ask, NoVA and the preview only answer from published (live) documents. Approve, then publish.</p>}
            <ul className="mf-list">
              {awaiting.map((d) => (
                <DocRow key={d.id} orgId={org.id} doc={d} index={index[d.id]} canEdit={canEdit} uid={user.uid} app={app} onChanged={refreshIndex} />
              ))}
            </ul>
          </ModuleFrame.Panel>
          <ModuleFrame.Panel title="Versions" meta="live corpus">
            {!live.length && <p className="cs-help">No published versions yet.</p>}
            <table className="mf-table">
              <thead><tr><th>Document</th><th>Version</th><th>Published</th><th>Index</th></tr></thead>
              <tbody>
                {live.map((d) => (
                  <tr key={d.id}>
                    <td>{d.title}</td>
                    <td>v{d.publishedVersion || d.version || 1}{(d.version || 1) > (d.publishedVersion || 1) ? <span className="mf-muted"> · draft v{d.version}</span> : null}</td>
                    <td>{d.publishedAt ? new Date(d.publishedAt).toLocaleDateString() : '—'}</td>
                    <td><IndexChip ix={index[d.id]} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ModuleFrame.Panel>
          <ModuleFrame.Panel title="Flagged answers" meta="review items the guardrails filed (uncertain or uncited answers)">
            {!queue.length && <p className="cs-help">No answers flagged. This is separate from documents awaiting approval on the left.</p>}
            <ul className="mf-list">
              {queue.map((r) => (
                <li key={r.id} className="mf-row">
                  <Chip tone="warn">{r.reason}</Chip>
                  <span className="mf-row-grow" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{(r.content || '').slice(0, 120)}</span>
                  <Link to={`/orgs/${org.id}/human-review?item=${r.id}`} className="mf-muted">decide →</Link>
                </li>
              ))}
            </ul>
          </ModuleFrame.Panel>
        </ModuleFrame.Columns>
      )}

      {tab === 'guardrails' && (
        <ModuleFrame.Columns cols={2}>
          <ModuleFrame.Panel title="Guardrail policies" meta={isAdmin ? 'enforced in the answer path, not in prompts' : 'admins change these'}>
            <GuardrailToggles org={org} isAdmin={isAdmin} />
          </ModuleFrame.Panel>
          <ModuleFrame.Panel title="Test guardrails" meta="ask with the policies above applied">
            <AskPane orgId={org.id} appId={app?.id || null} placeholder="Try a question that should be refused, masked, or escalated…" />
          </ModuleFrame.Panel>
        </ModuleFrame.Columns>
      )}
    </ModuleFrame>
  );
}

function IndexChip({ ix }) {
  if (!ix) return <Chip tone="muted">not indexed</Chip>;
  if (ix.indexStatus === 'done' || ix.indexStatus === 'indexed') return <Chip tone="ok">{ix.chunkCount || 0} chunks{ix.embedded ? ' · embedded' : ''}</Chip>;
  if (ix.indexStatus === 'error') return <Chip tone="danger">index error</Chip>;
  return <Chip tone="info">{ix.indexStatus || 'pending'}</Chip>;
}

function DocRow({ orgId, doc, index, canEdit, uid, app, onChanged, target = false }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [open, setOpen] = useState(target);
  const rowRef = useRef(null);
  useEffect(() => { if (target) rowRef.current?.scrollIntoView({ block: 'center' }); }, [target]);
  const run = async (fn) => {
    setBusy(true); setErr('');
    try { await fn(); onChanged && onChanged(); } catch (e) { setErr(friendlyError(e)); } finally { setBusy(false); }
  };
  const patch = (p) => updateEntity('knowledgeDoc', { org: orgId }, doc.id, p);
  const linked = app && (doc.appIds || []).includes(app.id);
  return (
    <li ref={rowRef} className={`mf-row${target ? ' is-target' : ''}`} style={{ flexDirection: 'column', alignItems: 'stretch', gap: 6 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <Chip tone={STATUS_TONE[doc.status] || 'muted'}>{doc.status}</Chip>
        <button type="button" className="mf-row-grow" style={{ background: 'none', border: 0, color: 'var(--text)', textAlign: 'left', cursor: 'pointer', fontWeight: 600, padding: 0 }} onClick={() => setOpen((v) => !v)}>
          {doc.title} <span className="mf-muted">v{doc.version || 1} · {doc.sourceType}{doc.audience === 'public' ? ' · public' : ''}</span>
        </button>
        {doc.status === 'live' && <IndexChip ix={index} />}
        {app && (
          <button type="button" className="cs-btn cs-btn-sm cs-btn-ghost" disabled={busy} title={linked ? `Unlink from ${app.name}` : `Link to ${app.name}`}
            onClick={() => run(() => patch({ appIds: linked ? (doc.appIds || []).filter((x) => x !== app.id) : [...(doc.appIds || []), app.id] }))}>
            {linked ? '● linked' : '○ link'}
          </button>
        )}
        {doc.status === 'draft' && <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" disabled={busy} onClick={() => run(() => patch({ status: 'uat' }))}>Submit for approval</button>}
        {doc.status === 'uat' && canEdit && <button type="button" className="cs-btn cs-btn-sm cs-btn-primary" disabled={busy} onClick={() => run(() => patch({ status: 'approved', approvedBy: uid, approvedAt: Date.now() }))}>Approve</button>}
        {doc.status === 'uat' && <button type="button" className="cs-btn cs-btn-sm cs-btn-ghost" disabled={busy} onClick={() => run(() => patch({ status: 'draft' }))}>Return to draft</button>}
        {doc.status === 'approved' && canEdit && <button type="button" className="cs-btn cs-btn-sm cs-btn-primary" disabled={busy} onClick={() => run(() => apiPost(`/knowledge/${orgId}/${doc.id}/publish`))}>Publish</button>}
        {doc.status === 'live' && canEdit && (doc.version || 1) > (doc.publishedVersion || 1) && <button type="button" className="cs-btn cs-btn-sm cs-btn-primary" disabled={busy} onClick={() => run(() => apiPost(`/knowledge/${orgId}/${doc.id}/publish`))}>Publish v{doc.version}</button>}
        {doc.status === 'live' && canEdit && <button type="button" className="cs-btn cs-btn-sm cs-btn-ghost" disabled={busy} onClick={() => run(() => apiPost(`/knowledge/${orgId}/${doc.id}/retire`))}>Retire</button>}
        {doc.status === 'retired' && <button type="button" className="cs-btn cs-btn-sm cs-btn-ghost" disabled={busy} onClick={() => run(() => patch({ status: 'draft' }))}>Reopen</button>}
      </div>
      {err && <p className="cs-form-error" role="alert" style={{ margin: 0 }}>{err}</p>}
      {open && <DocEditor orgId={orgId} doc={doc} onSaved={onChanged} />}
    </li>
  );
}

function DocEditor({ orgId, doc, onSaved }) {
  const [content, setContent] = useState(doc.content || '');
  const [state, setState] = useState('');
  const dirty = content !== (doc.content || '');
  const save = async () => {
    setState('saving');
    try {
      await updateEntity('knowledgeDoc', { org: orgId }, doc.id, {
        content, version: (doc.version || 1) + 1,
        // Edits to approved/live docs restart the approval path.
        ...(['approved', 'uat'].includes(doc.status) ? { status: 'draft' } : {}),
      });
      setState('saved'); onSaved && onSaved();
    } catch (e) { setState(friendlyError(e)); }
  };
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      <KV rows={[
        ['Source', doc.sourceRef ? <a href={doc.sourceRef} target="_blank" rel="noreferrer">{doc.sourceRef}</a> : doc.sourceType],
        ['Tags', (doc.tags || []).join(', ') || '—'],
        ['Approved', doc.approvedAt ? new Date(doc.approvedAt).toLocaleString() : '—'],
      ]} />
      <textarea className="mf-textarea" style={{ minHeight: 160 }} value={content} onChange={(e) => setContent(e.target.value)} aria-label="Document content" />
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button type="button" className="cs-btn cs-btn-sm cs-btn-primary" disabled={!dirty || state === 'saving'} onClick={save}>Save as v{(doc.version || 1) + 1}</button>
        <span className="mf-muted">{doc.status === 'live' ? 'Saving creates a new draft version; the live version keeps answering until you publish.' : ''}</span>
        {state && state !== 'saving' && <span className={state === 'saved' ? 'mf-muted' : 'cs-form-error'}>{state === 'saved' ? 'Saved.' : state}</span>}
      </div>
    </div>
  );
}

function AddSource({ orgId, uid, appId, onDone }) {
  const [mode, setMode] = useState('paste');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [url, setUrl] = useState('');
  const [audience, setAudience] = useState('internal');
  const [tags, setTags] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  // Upload: plain text reads in the browser; PDF / Word go to the raw bucket
  // and the backend extracts the text (same extractor NoVA uses for
  // attachments). Either way the text lands in the textarea for review.
  const [fileState, setFileState] = useState('');
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!title) setTitle(f.name.replace(/\.[a-z0-9]+$/i, ''));
    setErr('');
    const ext = f.name.split('.').pop().toLowerCase();
    if (ext === 'pdf' || ext === 'docx') {
      setFileState('Reading file…');
      try {
        if (!isUploadConfigured()) throw new Error('file uploads are not configured on this instance');
        const key = buildKey(`items/knowledge/${orgId}/`, f.name);
        const { key: usedKey } = await uploadFile(f, { key, contentType: f.type || 'application/octet-stream' });
        const r = await apiPost(`/knowledge/${orgId}/extract`, { key: usedKey, name: f.name, type: f.type, size: f.size });
        setContent(String(r.text || ''));
        setFileState(r.truncated ? 'Read (long document — text was trimmed).' : `Read ${f.name}. Check the text below, then add.`);
      } catch (ex) {
        setFileState('');
        setErr(friendlyError(ex));
      }
      return;
    }
    const reader = new FileReader();
    reader.onload = () => { setContent(String(reader.result || '').slice(0, 200000)); setFileState(`Read ${f.name}.`); };
    reader.readAsText(f);
  };
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      await createEntity('knowledgeDoc', { org: orgId }, {
        title: title.trim(), content: content.trim(), sourceType: mode, sourceRef: mode === 'url' ? url.trim() : '',
        audience, tags: tags.split(',').map((t) => t.trim()).filter(Boolean), appIds: appId ? [appId] : [], createdBy: uid,
      });
      setTitle(''); setContent(''); setUrl(''); setTags('');
      onDone();
    } catch (ex) { setErr(friendlyError(ex)); } finally { setBusy(false); }
  };
  return (
    <ModuleFrame.Panel title="Add a source" meta="lands as a draft; submit for approval when ready">
      <form onSubmit={submit} style={{ display: 'grid', gap: 8 }}>
        <div style={{ display: 'flex', gap: 6 }}>
          {['paste', 'upload', 'url'].map((m) => (
            <button key={m} type="button" className={`cs-btn cs-btn-sm ${mode === m ? 'cs-btn-primary' : 'cs-btn-outline'}`} onClick={() => setMode(m)}>{m}</button>
          ))}
        </div>
        <input className="cs-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title — e.g. Service SOP v2.1" aria-label="Title" />
        {mode === 'upload' && (
          <>
            <input type="file" accept=".pdf,.docx,.txt,.md,.markdown,.csv,.json,text/*,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document" onChange={onFile} aria-label="Upload a document (PDF, Word, text)" />
            <p className="mf-muted" style={{ margin: 0 }}>{fileState || 'PDF, Word (.docx), text, markdown, CSV or JSON — up to 8 MB. The text is extracted for you to review.'}</p>
          </>
        )}
        {mode === 'url' && <input className="cs-input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://… (reference; paste the text below for indexing)" aria-label="Source URL" />}
        <textarea className="mf-textarea" value={content} onChange={(e) => setContent(e.target.value)} placeholder="Document text (markdown is fine). This is what gets indexed." aria-label="Content" />
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <select className="cs-input" value={audience} onChange={(e) => setAudience(e.target.value)} aria-label="Audience">
            <option value="internal">internal — org members and their apps</option>
            <option value="public">public — safe for anonymous channels</option>
          </select>
          <input className="cs-input" style={{ flex: 1 }} value={tags} onChange={(e) => setTags(e.target.value)} placeholder="tags, comma separated" aria-label="Tags" />
        </div>
        {err && <p className="cs-form-error" role="alert">{err}</p>}
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="submit" className="cs-btn cs-btn-primary" disabled={busy || !title.trim() || content.trim().length < 20}>{busy ? 'Adding…' : 'Add as draft'}</button>
          <button type="button" className="cs-btn cs-btn-ghost" onClick={onDone}>Cancel</button>
        </div>
      </form>
    </ModuleFrame.Panel>
  );
}

function GuardrailToggles({ org, isAdmin }) {
  const [err, setErr] = useState('');
  const g = org.guardrails || {};
  const toggle = async (key) => {
    setErr('');
    // Through the policy API: a numbered, restorable revision + audit row.
    try { await savePolicy(org.id, 'guardrails', { ...g, [key]: !g[key] }); } catch (e) { setErr(friendlyError(e)); }
  };
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      {GUARDRAILS.map(([key, label, help]) => (
        <label key={key} className="mf-toggle">
          <input type="checkbox" checked={!!g[key]} onChange={() => toggle(key)} disabled={!isAdmin} />
          <div>
            <div style={{ fontWeight: 600 }}>{label}</div>
            <p className="cs-help" style={{ fontSize: 12 }}>{help}</p>
          </div>
        </label>
      ))}
      {err && <p className="cs-form-error" role="alert">{err}</p>}
      <p className="mf-muted">Policies apply instantly to every surface — preview, NoVA channels, voice tools, GraphQL ask — and to QA guardrail cases.</p>
      <Link to={withApp(`/orgs/${org.id}/qa`, null)} className="mf-muted">Add guardrail test cases in QA →</Link>
      <PolicyHistory org={org} isAdmin={isAdmin} area="guardrails" compact />
    </div>
  );
}
